import React, { Component } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import findIndex from 'lodash.findindex';

import ItemAddedDialog from '../dialogs/ItemAddedDialog';
import LoadingDialog from '../dialogs/LoadingDialog';
import LoadMoreButton from './LoadMoreButton';
import MediaListItem from './MediaListItem';
import MediaListMasonry from './MediaListMasonry';
import ThumbnailPreview from '../thumbnail/ThumbnailPreview';
import withRequestHandler from '../../hoc/withRequestHandler';
import { FormattedMessageWrappedInSpan } from '../../misc';

import AddFotoFlatToCartMutation from '../../../mutations/AddFotoFlatToCartMutation';
import AddSingleFotoToCartMutation from '../../../mutations/AddSingleFotoToCartMutation';
import RequestRecheckMutation from '../../../mutations/RequestRecheckMutation';

import { getFromLocalStorage, setToLocalStorage } from '../../../utils/browser-storage';
import { trackEvent } from '../../../utils/ga-tracking';

import {
  FOTO_FLAT,
  SINGLE_PHOTO,
  PARTICIPANT,
  IMPRESSION,
  ACCOUNT,
} from '../../../utils/variables';
import { getProduct } from '../../../utils/common';

class MediaList extends Component {
  constructor(props) {
    super(props);

    if (props.listType !== 'account') {
      this.fotoFlat = getProduct(props.offer.products, FOTO_FLAT);
      this.singlePhoto = getProduct(props.offer.products, SINGLE_PHOTO);
    }

    this.state = {
      slideIndex: 0,
      email: getFromLocalStorage('email') || '',
      updatedCart: null,
      showThumbnailPreview: false,
      showRecheckView: false,
      rechecks: [],
      showItemAddedDialog: false,
    };
  }

  handleCloseItemAddedDialog = () => {
    this.setState({ showItemAddedDialog: false });
  };

  handleOpenThumbnailPreview = mediaId => {
    const { edges } = this.props.media;
    const index = findIndex(edges, edge => edge.node.id === mediaId);
    if (index >= edges.length - 5) {
      if (this.props.hasMore) {
        this.props.onLoadMore();
      }
    }

    this.setState({
      showThumbnailPreview: true,
      slideIndex: index,
    });
  };

  handleCloseThumnailPreview = () => {
    this.setState({
      showThumbnailPreview: false,
      showRecheckView: false,
    });
  };

  handleShowRecheckView = () => {
    this.setState({ showRecheckView: true });
  };

  handleHideRecheckView = () => {
    this.setState({ showRecheckView: false });
  };

  handleSlide = index => {
    if (index >= this.props.media.edges.length - 5) {
      if (this.props.hasMore) {
        this.props.onLoadMore();
      }
    }

    this.setState({
      slideIndex: index,
    });
  };

  handleBuyFotoFlat = () => {
    this.props.onRequestStart();
    const { startnumber, relay } = this.props;
    AddFotoFlatToCartMutation.commit(
      relay.environment,
      this.fotoFlat.id,
      startnumber,
      (error, cart) => {
        if (error) {
          this.props.onRequestError(error);
        } else {
          this.setState({ updatedCart: cart, showItemAddedDialog: true });
        }
        this.props.onRequestStop();
      }
    );
  };

  handleBuySinglePhoto = () => {
    this.props.onRequestStart();
    const { relay, media } = this.props;
    AddSingleFotoToCartMutation.commit(
      relay.environment,
      this.singlePhoto.id, // product id
      media.edges[this.state.slideIndex].node.id, // foto media id
      (error, cart) => {
        if (error) {
          this.props.onRequestError(error);
        } else {
          this.setState({ updatedCart: cart, showItemAddedDialog: true });
        }
        this.props.onRequestStop();
      }
    );
  };

  handleRequestRecheck = (mediaId, startnumber, email) => {
    this.props.onRequestStart();
    this.setState({ showRecheckView: false });
    const { relay, media, intl } = this.props;
    RequestRecheckMutation.commit(
      relay.environment,
      email,
      intl.locale,
      media.edges[this.state.slideIndex].node.id,
      startnumber,
      (error, status) => {
        if (!error) {
          setToLocalStorage('email', email);
          const newRechecks = [...this.state.rechecks, mediaId];
          this.setState({
            email,
            rechecks: newRechecks,
          });
        } else {
          this.props.onRequestError(error);
        }
        this.props.onRequestStop();
      }
    );
  };

  render() {
    const {
      eventHasSearchByFace,
      eventHasSecondFactor,
      media,
      hasMore,
      startNumbers,
      hasMorePhotosButton,
      isFetching,
      isLoading,
      canRestartUpload,
      participantMediaCount,
      impressionMediaCount,
      startnumber,
      eventSgId,
      listType,
      onLoadMore,
      onScrollToImpressions,
      onScrollToVoucher,
      onScrollToExtendedSearch,
      onImagesLoaded,
    } = this.props;

    const {
      updatedCart,
      slideIndex,
      email,
      rechecks,
      showThumbnailPreview,
      showRecheckView,
      showItemAddedDialog,
    } = this.state;

    const fotoFlatOffer = this._getFotoFlatOffer();

    return (
      <div className="row">
        <div className="col-xs-16 col-sm-12 col-sm-offset-2">
          <MediaListMasonry
            participantMediaCount={participantMediaCount}
            impressionMediaCount={impressionMediaCount}
            fotoFlatOffer={fotoFlatOffer}
            onBuyFotoFlatClick={this.handleBuyFotoFlat}
            onScrollToImpressions={onScrollToImpressions}
            onScrollToVoucher={onScrollToVoucher}
            onImagesLoaded={onImagesLoaded}
            listType={listType}
          >
            {media.edges.map(({ node: mediaNode }) => (
              <MediaListItem
                key={mediaNode.id}
                item={mediaNode}
                onShowPreview={this.handleOpenThumbnailPreview}
                isAccountEventPage={listType === ACCOUNT}
              />
            ))}
          </MediaListMasonry>
          {hasMore && <LoadMoreButton showSpinner={isFetching} onClick={onLoadMore} />}
          {hasMorePhotosButton && !hasMore && listType === 'participant' && (
            <div className="medialist-scroll-to-extended-search">
              <button
                className="primary"
                onClick={() => {
                  trackEvent('Event', 'Search', 'Are there more photos of me button');
                  onScrollToExtendedSearch();
                }}
              >
                <FormattedMessageWrappedInSpan
                  id="mediaList.extendedSearchHintButton"
                  defaultMessage="+ Are there more photos of me?"
                />
              </button>
            </div>
          )}
          <ThumbnailPreview
            eventHasSearchByFace={eventHasSearchByFace}
            eventHasSecondFactor={eventHasSecondFactor}
            open={!showItemAddedDialog && showThumbnailPreview}
            media={media}
            eventSgId={eventSgId}
            rechecks={rechecks}
            slideIndex={slideIndex}
            startnumber={startnumber}
            fotoFlatProduct={fotoFlatOffer}
            singlePhotoProduct={this.singlePhoto}
            showRecheckView={showRecheckView}
            email={email}
            startNumbers={startNumbers}
            isLoading={isLoading}
            isAccountEventPage={listType === ACCOUNT}
            canRestartUpload={canRestartUpload}
            onSlide={this.handleSlide}
            onBuySinglePhoto={this.handleBuySinglePhoto}
            onBuyFotoFlat={this.handleBuyFotoFlat}
            onShowRecheckView={this.handleShowRecheckView}
            onHideRecheckView={this.handleHideRecheckView}
            onRequestRecheck={this.handleRequestRecheck}
            onRequestClose={this.handleCloseThumnailPreview}
          />
          {updatedCart && (
            <ItemAddedDialog
              open={showItemAddedDialog}
              lineItems={updatedCart.lineItems}
              totalPrice={updatedCart.totalPrice}
              onRequestClose={this.handleCloseItemAddedDialog}
            />
          )}
          <LoadingDialog open={isLoading} />
        </div>
      </div>
    );
  }

  _getFotoFlatOffer() {
    const { offer, listType } = this.props;

    // 1.
    // The Foto-Flat offer should only be visible in the participant list and the impressions
    // list
    if (listType !== PARTICIPANT && listType !== IMPRESSION) {
      return null;
    }

    const fotoFlatProduct = getProduct(offer.products, FOTO_FLAT);
    const singlePhotoProduct = getProduct(offer.products, SINGLE_PHOTO);

    // 2.
    // The Foto-Flat should only be shown if the the customer would make a good deal
    if (fotoFlatProduct) {
      if (this._shouldFotoFlatBeDisplayed(fotoFlatProduct, singlePhotoProduct)) {
        return {
          id: fotoFlatProduct.id,
          price: fotoFlatProduct.price,
          voucher: offer.voucherCampaign,
        };
      }
    }

    return null;
  }

  _shouldFotoFlatBeDisplayed(fotoFlat, singlePhoto) {
    // When an event does not offer a single photo product the FotoFlat should definetly
    // be displayed
    if (!singlePhoto) {
      return true;
    }

    // The Foto-Flat offer should only be shown if the price of found photos for a
    // participant in their sum is higher than the price of a Foto-Flat
    let minCountNecessary = 10000;
    if (singlePhoto.price.amount !== 0) { // avoid division by zero
      minCountNecessary = fotoFlat.price.amount / singlePhoto.price.amount;
    }
    return this.props.participantMediaCount >= minCountNecessary;
  }
}

MediaList.defaultProps = {
  eventHasSearchByFace: false,
  eventHasSecondFactor: false,
};

MediaList.propTypes = {
  eventHasSearchByFace: PropTypes.bool,
  eventHasSecondFactor: PropTypes.bool,
  media: PropTypes.object,
  offer: PropTypes.object,
  hasMore: PropTypes.bool,
  hasMorePhotosButton: PropTypes.bool,
  isFetching: PropTypes.bool,
  isLoading: PropTypes.bool,
  canRestartUpload: PropTypes.bool,
  participantMediaCount: PropTypes.number,
  impressionMediaCount: PropTypes.number,
  eventSgId: PropTypes.number,
  startnumber: PropTypes.string,
  startNumbers: PropTypes.array,
  onLoadMore: PropTypes.func,
  onScrollToImpressions: PropTypes.func,
  onScrollToVoucher: PropTypes.func,
  onScrollToExtendedSearch: PropTypes.func,
  onRequestStart: PropTypes.func,
  onRequestStop: PropTypes.func,
  onRequestError: PropTypes.func,
  listType: PropTypes.oneOf([
    'participant',
    'categories',
    'suggestions',
    'gps',
    'face',
    'impressions',
    'account',
  ]).isRequired,
  relay: PropTypes.object,
  intl: PropTypes.object,
  onImagesLoaded: PropTypes.func,
};

export { MediaList };

const WrappedComponent = withRequestHandler(MediaList);

export default createFragmentContainer(injectIntl(WrappedComponent), {
  media: graphql`
    fragment MediaList_media on MediaConnection {
      edges {
        node {
          id
          sgId
          shotDate
          variants {
            name
            url
          }
          ...MediaListItem_item
        }
      }
    }
  `,
  offer: graphql`
    fragment MediaList_offer on Event {
      products {
        id
        mediaCollection {
          name
        }
        price
        type
      }
      voucherCampaign {
        banner
        name
        shortText
      }
    }
  `,
});
